import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Warning, PencilSimple, CloudArrowDown, Trash, Notebook } from "@phosphor-icons/react";

const getContentIcon = () => Notebook;

const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export function DraftRecoveryBanner({ drafts, onResume, onPreview, onDiscard }) {
  const [expanded, setExpanded] = useState(false);

  if (drafts.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 border border-amber-500/30 rounded-lg overflow-hidden bg-amber-500/5"
      data-testid="draft-recovery-banner"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="draft-recovery-toggle"
      >
        <div className="flex items-center gap-2">
          <Warning size={16} className="text-amber-400" weight="fill" />
          <span className="text-sm font-medium text-amber-400">
            You have {drafts.length} unsaved draft{drafts.length !== 1 ? "s" : ""} from the last 7 days
          </span>
        </div>
        <span className="text-xs text-[var(--text-muted)]">{expanded ? "Hide" : "View Drafts"}</span>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-2">
              {drafts.map((draft) => {
                const Icon = getContentIcon(draft.content_type);
                return (
                  <div
                    key={draft.draft_id}
                    className="flex items-center justify-between gap-3 p-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg"
                    data-testid={`draft-card-${draft.draft_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Icon size={18} className="text-amber-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-[var(--text-primary)] truncate font-medium">
                          {draft.generation_context?.topic || draft.content_type || "Untitled Draft"}
                        </p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {draft.content_type} &middot; Last saved {relativeTime(draft.updated_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => onResume(draft)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors" data-testid={`draft-resume-${draft.draft_id}`}>
                        <PencilSimple size={14} />Resume Editing
                      </button>
                      <button onClick={() => onPreview(draft)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[#FF4400]/10 text-[#FF4400] border border-[#FF4400]/20 hover:bg-[#FF4400]/20 transition-colors" data-testid={`draft-restore-${draft.draft_id}`}>
                        <CloudArrowDown size={14} />Preview
                      </button>
                      <button onClick={() => onDiscard(draft.draft_id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`draft-discard-${draft.draft_id}`}>
                        <Trash size={14} />Discard
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
